import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import {
  formatDate,
  formatDateTime,
  formatDays,
  formatMinutes,
  formatMoney,
  formatNumber,
  formatShortTime,
} from '../lib/format';
import { useWaitingShortDays } from '../lib/shortfall';
import { MyShortDays, ShortfallQueue } from './ShortDays';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  statusTone,
  useToast,
} from '../components/ui';

type Scope = 'all' | 'review';

/**
 * Route: `/violations`. The tab lives in the URL (`?tab=review`); a penalty's
 * row opens its own page, where its notifications open too.
 */
export function ViolationsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  // Only an administrator decides a penalty or an objection to one (2026-10-22); a department
  // leader sees their team's penalties and decides none.
  const reviewer = can(P.PENALTIES_MANAGE);

  const waiting = useQuery({
    queryKey: ['penalties', 'review', 'count'],
    queryFn: () => api.get<Paginated<unknown>>('/penalties', { scope: 'review', pageSize: 1 }),
    enabled: reviewer,
  });
  // Days more than an hour short, waiting for an administrator (CR-004).
  const shortDays = useWaitingShortDays();

  const tabs = [
    { id: 'penalties', label: t('violations.penalties') },
    ...(reviewer ? [{ id: 'review', label: t('violations.review'), count: waiting.data?.total }] : []),
    ...(reviewer ? [{ id: 'shortfall', label: t('shortfall.tab'), count: shortDays }] : []),
    ...(can(P.REPORTS_VIEW)
      ? [
          { id: 'violations', label: t('violations.violations') },
          { id: 'summary', label: t('violations.byEmployee') },
        ]
      : []),
  ];
  const requested = params.get('tab');
  const tab = tabs.some((x) => x.id === requested) ? requested! : 'penalties';

  return (
    <>
      <PageHeader title={t('violations.title')} />
      <Tabs active={tab} onChange={(id) => setParams(id === 'penalties' ? {} : { tab: id }, { replace: true })} tabs={tabs} />
      <div className="mt-5">
        {/* One's own days more than an hour short: waiting ones say nothing is deducted yet. */}
        {tab === 'penalties' && <MyShortDays />}
        {tab === 'penalties' && <PenaltyList scope="all" />}
        {tab === 'review' && <PenaltyList scope="review" />}
        {tab === 'shortfall' && <ShortfallQueue />}
        {(tab === 'violations' || tab === 'summary') && !can(P.USERS_VIEW_ALL) && (
          <p className="mb-3 text-caption text-muted">{t('reach.figuresNote')}</p>
        )}
        {tab === 'violations' && <ViolationList />}
        {tab === 'summary' && <PenaltySummary />}
      </div>
    </>
  );
}

/** A work date (a `@db.Date`, midnight UTC) as that calendar day, wherever the reader is. */
export function formatWorkDate(value: string, locale: 'en' | 'ar'): string {
  return formatDate(`${value.slice(0, 10)}T12:00:00`, locale);
}

/** Whole minutes a shift ended before its rostered end — floored, as the API counts them (2026-11-03). */
export function minutesBeforeEnd(endedAt: string, rosterEndAt: string): number {
  return Math.max(0, Math.floor((new Date(rosterEndAt).getTime() - new Date(endedAt).getTime()) / 60_000));
}

/** How the employee's objection to a penalty stands — CLOSED once the penalty was cancelled some other way. */
export type ObjectionStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'CLOSED';

export const OBJECTION_TONE: Record<ObjectionStatus, BadgeTone> = {
  PENDING: 'warning',
  ACCEPTED: 'success',
  REJECTED: 'error',
  CLOSED: 'neutral',
};

/** "Objection waiting", "Objection approved"… beside a penalty's status. */
export function ObjectionBadge({ status }: { status: ObjectionStatus }) {
  const { t } = useI18n();
  return <Badge tone={OBJECTION_TONE[status]}>{t(`violations.objectionBadge.${status}` as TranslationKey)}</Badge>;
}

/**
 * What a penalty is for: a break past its limit, inactivity past its threshold, a
 * day short of its Daily hours, a shift ended earlier than it allows, or the reason
 * it was raised with.
 */
export function PenaltyWhat({ penalty }: { penalty: any }) {
  const { t, locale, localized } = useI18n();
  const brk = penalty.attendanceBreak;
  const idle = penalty.idleSession;
  const short = penalty.dayReview;
  const early = penalty.earlyEndReview;
  if (early?.earlyEndAt) {
    return (
      <>
        <span className="block truncate">{t('violations.earlyEnd')}</span>
        <span className="text-caption text-faint">
          {formatWorkDate(early.workDate, locale)}
          {' · '}
          <span className="tabular">
            {t('violations.earlyEndAt', {
              ended: formatShortTime(early.earlyEndAt, locale),
              early: formatMinutes(minutesBeforeEnd(early.earlyEndAt, early.earlyEndRosterEndAt), locale),
              end: formatShortTime(early.earlyEndRosterEndAt, locale),
            })}
          </span>
        </span>
      </>
    );
  }
  if (short) {
    // More than an hour short: an administrator decided a percentage of a day's pay (CR-004) —
    // this penalty's own, from its days: a reversed decision's case may say another since.
    const decidedPercent =
      penalty.sourceType === 'ATTENDANCE_SHORTFALL' && penalty.days != null ? Math.round(Number(penalty.days) * 100) : null;
    return (
      <>
        <span className="block truncate">{t('violations.shortDay')}</span>
        <span className="text-caption text-faint">
          {formatWorkDate(short.workDate, locale)}
          {short.requiredMinutes != null && (
            <>
              {' · '}
              <span className="tabular">
                {t('violations.shortDayWorked', {
                  worked: formatMinutes(short.workedMinutes, locale),
                  owed: formatMinutes(short.requiredMinutes, locale),
                })}
              </span>
            </>
          )}
          {decidedPercent != null && <span className="block">{t('violations.shortfallDecision', { percent: decidedPercent })}</span>}
        </span>
      </>
    );
  }
  if (idle) {
    return (
      <>
        <span className="block truncate">
          {t('violations.idle', { reason: t(`presence.reason.${idle.reason}` as TranslationKey) })}
        </span>
        <span className="text-caption text-faint">
          {t('violations.idleFrom', { time: formatDateTime(idle.lastActivityAt, locale) })}
          {' · '}
          <Link
            to={`/attendance/sessions/${idle.sessionId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            {t('violations.idleShift')}
          </Link>
        </span>
      </>
    );
  }
  if (brk) {
    const length = brk.endedAt
      ? Math.floor((new Date(brk.endedAt).getTime() - new Date(brk.startedAt).getTime()) / 60_000)
      : null;
    return (
      <>
        <span className="block truncate">{t('violations.breakOver', { type: localized(brk.breakType) })}</span>
        <span className="text-caption text-faint">
          {formatDateTime(brk.startedAt, locale)}
          {length != null && brk.breakType.maxMinutes != null && (
            <>
              {' · '}
              <span className="tabular">
                {t('violations.breakLength', {
                  length: formatMinutes(length, locale),
                  limit: formatMinutes(brk.breakType.maxMinutes, locale),
                })}
              </span>
            </>
          )}
        </span>
      </>
    );
  }
  return (
    <>
      <span className="block truncate">{penalty.reason}</span>
      {penalty.violation && (
        <span className="text-caption text-faint">
          {enumLabel(penalty.violation.type, locale)}
          {penalty.violation.ticketId && (
            <>
              {' · '}
              <Link
                to={`/tickets/${penalty.violation.ticketId}`}
                onClick={(e) => e.stopPropagation()}
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t('nav.tickets')}
              </Link>
            </>
          )}
        </span>
      )}
    </>
  );
}

function PenaltyList({ scope }: { scope: Scope }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const review = scope === 'review';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['penalties', scope, page, filters],
    queryFn: () =>
      api.get<Paginated<any> & { totalAmount: number; totalDays: number }>('/penalties', {
        page,
        pageSize: 25,
        ...(review ? { scope } : filters),
      }),
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/penalties/${id}/approve`),
    onSuccess: () => {
      toast.success(t('violations.approvedToast'));
      void queryClient.invalidateQueries({ queryKey: ['penalties'] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <>
      {!review && (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-3">
            {/* Penalties are days of salary; their money is what payroll takes at each person's day rate. */}
            <Metric label={t('violations.totalDays')} value={formatDays(data?.totalDays ?? 0, locale)} tone="warning" />
            <Metric
              label={t('violations.valueAtRate')}
              value={formatMoney(data?.totalAmount ?? 0, 'EGP', locale)}
              hint={t('violations.estimate')}
            />
            <Metric label={t('violations.penalties')} value={formatNumber(data?.total ?? 0, locale)} />
          </div>

          <Card className="mb-4" padded={false}>
            <div className="grid gap-3 p-4 sm:grid-cols-3">
              <Select value={filters.status ?? ''} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">{t('common.status')} — {t('common.all')}</option>
                {['PENDING', 'APPROVED', 'APPLIED_TO_PAYROLL', 'CANCELLED'].map((s) => (
                  <option key={s} value={s}>{enumLabel(s, locale)}</option>
                ))}
              </Select>
              <Input
                type="month"
                value={filters.effectiveMonth ?? ''}
                onChange={(e) => setFilters({ ...filters, effectiveMonth: e.target.value })}
              />
            </div>
          </Card>
        </>
      )}

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<ShieldAlert className="h-8 w-8" />}
            title={review ? t('violations.nothingToReview') : t('dash.nothingPending')}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('nav.employees')}</Th>
                  <Th>{t('common.reason')}</Th>
                  <Th>{t('violations.effectiveMonth')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('violations.days')}</Th>
                  <Th align="end">{t('common.amount')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((penalty) => {
                  const decidedBy =
                    penalty.status === 'CANCELLED' ? penalty.cancelledBy : penalty.status === 'APPROVED' ? penalty.approvedBy : null;
                  return (
                    <Tr key={penalty.id} onClick={() => navigate(`/violations/penalties/${penalty.id}`)}>
                      <Td>{localized(penalty.user)}</Td>
                      <Td className="max-w-sm">
                        <PenaltyWhat penalty={penalty} />
                      </Td>
                      <Td className="whitespace-nowrap tabular text-muted">
                        <span dir="ltr">{penalty.effectiveMonth}</span>
                      </Td>
                      <Td>
                        <Badge tone={statusTone(penalty.status)}>{enumLabel(penalty.status, locale)}</Badge>
                        {penalty.objection && (
                          <span className="mt-1 block">
                            <ObjectionBadge status={penalty.objection.status} />
                          </span>
                        )}
                        {decidedBy && (
                          <span className="mt-0.5 block text-caption text-muted">
                            {t('violations.by', { name: localized(decidedBy) })}
                          </span>
                        )}
                        {/* Dropped because the day's hours were worked, or cancelled with the employee's
                            wallet points: said in the reader's language. */}
                        {penalty.status === 'CANCELLED' && penalty.waivedBy ? (
                          <span className="block max-w-[14rem] truncate text-caption text-faint">{t('violations.waivedShort')}</span>
                        ) : penalty.status === 'CANCELLED' && penalty.walletRedemption ? (
                          <span className="block max-w-[14rem] truncate text-caption text-faint">
                            {t('violations.walletCancelled', { points: formatNumber(penalty.walletRedemption.pointsSpent, locale) })}
                          </span>
                        ) : (
                          penalty.status === 'CANCELLED' &&
                          penalty.cancellationReason && (
                            <span className="block max-w-[14rem] truncate text-caption text-faint" title={penalty.cancellationReason}>
                              {penalty.cancellationReason}
                            </span>
                          )
                        )}
                      </Td>
                      <Td align="end" className="whitespace-nowrap tabular text-error-fg dark:text-error-dark">
                        {penalty.days != null ? formatDays(penalty.days, locale) : '—'}
                      </Td>
                      <Td align="end" className="whitespace-nowrap tabular text-muted">
                        {/* Until payroll prices it, a day-based penalty's money is an estimate. */}
                        {penalty.days != null && penalty.status !== 'APPLIED_TO_PAYROLL' && Number(penalty.amount) === 0 ? (
                          // Days of no salary: nothing on record for its day, not a penalty worth nothing.
                          <span className="text-warning-fg dark:text-warning-dark" title={t('violations.noSalaryHint')}>
                            {t('violations.noSalary')}
                          </span>
                        ) : (
                          <span title={penalty.days != null && penalty.status !== 'APPLIED_TO_PAYROLL' ? t('violations.estimate') : undefined}>
                            {penalty.days != null && penalty.status !== 'APPLIED_TO_PAYROLL' && '≈ '}
                            {formatMoney(penalty.amount, penalty.currency, locale)}
                          </span>
                        )}
                      </Td>
                      <Td align="end" className="whitespace-nowrap">
                        <span onClick={(e) => e.stopPropagation()}>
                          {/* An objection waiting comes first: its penalty's Approve and Cancel step aside. */}
                          {penalty.can?.decideObjection && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/violations/penalties/${penalty.id}/objection/approve`)}
                              >
                                {t('violations.approveObjection')}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/violations/penalties/${penalty.id}/objection/reject`)}
                              >
                                {t('violations.rejectObjection')}
                              </Button>
                            </>
                          )}
                          {penalty.can?.object && (
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/violations/penalties/${penalty.id}/object`)}>
                              {t('violations.object')}
                            </Button>
                          )}
                          {penalty.can?.approve && (
                            <Button size="sm" variant="ghost" disabled={approve.isPending} onClick={() => approve.mutate(penalty.id)}>
                              {t('common.approve')}
                            </Button>
                          )}
                          {penalty.can?.cancel && (
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/violations/penalties/${penalty.id}/cancel`)}>
                              {t('common.cancel')}
                            </Button>
                          )}
                          {penalty.status === 'APPLIED_TO_PAYROLL' && (
                            <span className="text-caption text-faint">{t('violations.appliedToPayroll')}</span>
                          )}
                        </span>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

function ViolationList() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['violations', page, filters],
    queryFn: () => api.get<Paginated<any>>('/violations', { page, pageSize: 25, ...filters }),
  });

  return (
    <>
      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <Select value={filters.type ?? ''} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">{t('violations.type')} — {t('common.all')}</option>
            {['VIEW_SLA_MISSED', 'ASSIGNMENT_SLA_MISSED', 'DEPARTMENT_VIEW_SLA_BREACH'].map((s) => (
              <option key={s} value={s}>{enumLabel(s, locale)}</option>
            ))}
          </Select>
          <Select value={filters.status ?? ''} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">{t('common.status')} — {t('common.all')}</option>
            <option value="OPEN">{enumLabel('OPEN', locale)}</option>
            <option value="VOIDED">{enumLabel('VOIDED', locale)}</option>
          </Select>
          <Input
            type="month"
            value={filters.month ?? ''}
            onChange={(e) => setFilters({ ...filters, month: e.target.value })}
          />
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<ShieldAlert className="h-8 w-8" />} title={t('dash.nothingPending')} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('violations.type')}</Th>
                  <Th>{t('nav.employees')}</Th>
                  <Th>{t('nav.tickets')}</Th>
                  <Th>{t('violations.deadline')}</Th>
                  <Th>{t('violations.actualAction')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((v) => (
                  <Tr key={v.id}>
                    <Td>
                      <Badge tone={v.type === 'VIEW_SLA_MISSED' ? 'warning' : 'error'}>
                        {enumLabel(v.type, locale)}
                      </Badge>
                    </Td>
                    <Td>{v.user ? localized(v.user) : <span className="text-faint">{localized(v.department)}</span>}</Td>
                    <Td>
                      {v.ticket ? (
                        <Link to={`/tickets/${v.ticket.id}`} className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">
                          {v.ticket.number}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td className="tabular text-muted">{formatDateTime(v.deadlineAt, locale)}</Td>
                    <Td className="tabular text-muted">
                      {v.actualActionAt ? formatDateTime(v.actualActionAt, locale) : <span className="text-error-fg dark:text-error-dark">—</span>}
                    </Td>
                    <Td><Badge tone={v.status === 'VOIDED' ? 'neutral' : 'error'}>{enumLabel(v.status, locale)}</Badge></Td>
                    <Td align="end">
                      {/* Voiding cancels its penalties: an administrator's (2026-10-22). */}
                      {can(P.TICKETS_OVERRIDE_SLA, P.PENALTIES_MANAGE) && v.status === 'OPEN' && (
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/violations/${v.id}/void`)}>
                          {t('violations.void')}
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>

    </>
  );
}

function PenaltySummary() {
  const { t, locale, localized } = useI18n();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const { data, isLoading } = useQuery({
    queryKey: ['penalties', 'summary', month],
    queryFn: () => api.get<any[]>('/penalties/summary', { month }),
  });

  return (
    <Card padded={false}>
      <CardHeader
        title={t('violations.byEmployee')}
        className="p-5 pb-0"
        action={<Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="!w-auto" />}
      />
      {isLoading ? (
        <LoadingBlock />
      ) : !data?.length ? (
        <EmptyState title={t('dash.nothingPending')} hint="" />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('nav.employees')}</Th>
              <Th align="end">{t('violations.penalties')}</Th>
              <Th align="end">{t('violations.days')}</Th>
              <Th align="end">{t('common.total')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <Tr key={row.user?.id ?? Math.random()}>
                <Td>{localized(row.user)}</Td>
                <Td align="end" className="tabular">{row.count}</Td>
                <Td align="end" className="whitespace-nowrap tabular text-error-fg dark:text-error-dark">{formatDays(row.days, locale)}</Td>
                <Td align="end" className="tabular text-muted">{formatMoney(row.total, 'EGP', locale)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
